<script lang="ts" setup>
import type {Page} from "#magnolia-layer/types/magnolia-types";
import {useMagnoliaSitePath} from "#magnolia-layer/composables/use-magnolia-site-path";
import {joinURL} from "ufo";
import {PhHouse} from "@phosphor-icons/vue";

const props = defineProps<{
  lookupPath?: string;
  currentLocation?: string;
}>();

const magnoliaContext = useMagnoliaContext();
const config = useRuntimeConfig();
const sitePath = useMagnoliaSitePath()!;

const lookupPath = props.lookupPath ?? magnoliaContext.nodePath ?? "";
const isOriginalPath = lookupPath === magnoliaContext.nodePath;

const {data} = await useMagnoliaFetch<Page>(
  joinURL(config.public.mgnl.apiPageNav, sitePath),
  {
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key];
    },
  },
);

function traverseNodes(obj: Page): Record<string, Page> {
  const result: Record<string, Page> = {};

  function recurse(node: Page) {
    result[node["@path"]] = node;
    const nodes = node["@nodes"]
      .map((nodeName) => node[nodeName])
      .filter((x): x is Page => x);
    for (const subNode of nodes) {
      recurse(subNode);
    }
  }

  recurse(obj);
  return result;
}

function getNodesForPath(dict: Record<string, Page>, path: string): Page[] {
  const nodes: Page[] = [];
  const parts = path.split("/").filter(Boolean); // Split path and remove empty parts
  let currentPath = "";
  for (const part of parts) {
    currentPath += `/${part}`;
    if (dict[currentPath]) {
      nodes.push(dict[currentPath]);
    }
  }
  return nodes.length ? nodes : [dict[sitePath]];
}

const dict = computed(() => (data.value ? traverseNodes(data.value) : null));
const relatedNodes = computed(() =>
  dict.value ? getNodesForPath(dict.value, lookupPath) : [],
);

const resolveTitle = (node: Page) => node["title"] ?? node["@name"];

const isLast = (index: number, array: any[]) => index === array.length - 1;

const parents = computed(() => {
  const nodes = relatedNodes.value;
  return isOriginalPath ? nodes.slice(0, -1) : nodes;
});

const currentLocation = computed(() => {
  const nodes = relatedNodes.value;
  return (
    props.currentLocation ??
    (isOriginalPath ? resolveTitle(nodes.at(-1)!) : null)
  );
});
</script>

<template>
  <nav :aria-label="$t('navigation.breadcrumb')" class="my-7">
    <ol class="flex gap-2 overflow-hidden max-w-full">
      <li v-for="(parent, index) in parents" class="flex gap-2 items-center">
        <NuxtLinkLocale
          :aria-label="
            stripPrefix(parent['@path']) === '/'
              ? $t('navigation.home')
              : undefined
          "
          :to="stripPrefix(parent['@path'])"
          class="text-sunrise-200 link-with-icon link-underline !font-serif">
          <template v-if="stripPrefix(parent['@path']) === '/'">
            <PhHouse class="text-2xl"></PhHouse>
          </template>
          <template v-else>{{ resolveTitle(parent) }}</template>
        </NuxtLinkLocale>
        <span
          v-if="!isLast(index, parents) || currentLocation"
          aria-hidden="true"
          >/</span
        >
      </li>
      <li
        aria-current="location"
        class="overflow-ellipsis text-nowrap max-w-full overflow-hidden">
        {{ currentLocation }}
      </li>
    </ol>
  </nav>
</template>

<style lang="scss" scoped></style>
